import {useState, useEffect, useCallback} from 'react';
import _ from 'lodash';
import {Table, Tag, Input, Form, Button, Avatar} from 'antd';
import {UserOutlined, MailOutlined, CopyOutlined} from '@ant-design/icons';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {GET_DABA_POWER_USER_REFERRALS} from '../resolvers';
import styles from './dabaPower.module.scss';
import UserLink from 'components/userLink/UserLink';

export const ActiveReferrersTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [minActiveReferrals, setMinActiveReferrals] = useState(0);
  const [debouncedMinActiveReferrals, setDebouncedMinActiveReferrals] =
    useState(0);
  const [minInactiveReferrals, setMinInactiveReferrals] = useState(0);
  const [debouncedMinInactiveReferrals, setDebouncedMinInactiveReferrals] =
    useState(0);

  const debouncedSetSearch = useCallback(
    _.debounce(value => setDebouncedSearch(value), 400),
    [],
  );
  const debouncedSetMinActiveReferrals = useCallback(
    _.debounce(value => setDebouncedMinActiveReferrals(value), 400),
    [],
  );
  const debouncedSetMinInactiveReferrals = useCallback(
    _.debounce(value => setDebouncedMinInactiveReferrals(value), 400),
    [],
  );

  useEffect(() => {
    debouncedSetSearch(search);
    return () => debouncedSetSearch.cancel?.();
  }, [search]);
  useEffect(() => {
    debouncedSetMinActiveReferrals(minActiveReferrals);
    return () => debouncedSetMinActiveReferrals.cancel?.();
  }, [minActiveReferrals]);
  useEffect(() => {
    debouncedSetMinInactiveReferrals(minInactiveReferrals);
    return () => debouncedSetMinInactiveReferrals.cancel?.();
  }, [minInactiveReferrals]);

  const {data, loading, refetch} = useQuery(GET_DABA_POWER_USER_REFERRALS, {
    variables: {
      filters: {
        search: debouncedSearch || undefined,
        minActiveReferrals:
          debouncedMinActiveReferrals > 0
            ? debouncedMinActiveReferrals
            : undefined,
        minInactiveReferrals:
          debouncedMinInactiveReferrals > 0
            ? debouncedMinInactiveReferrals
            : undefined,
        paginate: true,
        page: currentPage,
        limit,
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error?.message),
  });

  useEffect(() => {
    setCurrentPage(1);
    refetch({
      filters: {
        search: debouncedSearch || undefined,
        minActiveReferrals:
          debouncedMinActiveReferrals > 0
            ? debouncedMinActiveReferrals
            : undefined,
        minInactiveReferrals:
          debouncedMinInactiveReferrals > 0
            ? debouncedMinInactiveReferrals
            : undefined,
        paginate: true,
        page: 1,
        limit,
      },
    });
  }, [
    debouncedSearch,
    debouncedMinActiveReferrals,
    debouncedMinInactiveReferrals,
  ]);

  const handleReset = () => {
    setSearch('');
    setDebouncedSearch('');
    setMinActiveReferrals(0);
    setDebouncedMinActiveReferrals(0);
    setMinInactiveReferrals(0);
    setDebouncedMinInactiveReferrals(0);
    setCurrentPage(1);
    debouncedSetSearch.cancel?.();
    debouncedSetMinActiveReferrals.cancel?.();
    debouncedSetMinInactiveReferrals.cancel?.();
    refetch({
      filters: {
        paginate: true,
        page: 1,
        limit,
      },
    });
  };

  const handleTableChange = pagination => {
    setCurrentPage(pagination.current);
    setLimit(pagination.pageSize);
    refetch({
      filters: {
        search: debouncedSearch || undefined,
        minActiveReferrals:
          debouncedMinActiveReferrals > 0
            ? debouncedMinActiveReferrals
            : undefined,
        minInactiveReferrals:
          debouncedMinInactiveReferrals > 0
            ? debouncedMinInactiveReferrals
            : undefined,
        paginate: true,
        page: pagination.current,
        limit: pagination.pageSize,
      },
    });
  };

  const response = data?.getDabaPowerUserReferrals;
  const responseData =
    response?.__typename === 'ResponseWithDabaPowerUserReferrals'
      ? response?.data || {}
      : {};
  const dataSource = responseData?.users || [];
  const paginationData = response?.pagination || {};
  const ambassadorEligibleCount = responseData?.ambassadorEligibleCount ?? 0;

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const name = [record?.user?.firstName, record?.user?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        return (
          <div className={styles.userCell}>
            <Avatar src={record?.user?.imageUrl} icon={<UserOutlined />} />
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span className={styles.userName}>
                <UserLink userId={record?.user?.id}>
                  {name || record?.user?.email || record?.user?.id || '—'}
                </UserLink>
              </span>
              <span className={styles.userEmail}>
                {record?.user?.email || '—'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Location',
      key: 'location',
      dataIndex: 'user',
      render: val =>
        val?.country
          ? `${val?.city ? `${val?.city}, ` : ''}${val?.country}`.trim()
          : '—',
    },
    {
      title: 'Total Referrals',
      dataIndex: 'totalReferrals',
      key: 'totalReferrals',
    },
    {
      title: 'Active Referrals',
      dataIndex: 'activeReferrals',
      key: 'activeReferrals',
      render: val => <Tag color="green">{val ?? 0}</Tag>,
    },
    {
      title: 'Inactive Referrals',
      dataIndex: 'inactiveReferrals',
      key: 'inactiveReferrals',
      render: val => <Tag color="default">{val ?? 0}</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 260,
      render: (_, record) => {
        const email = record?.user?.email;
        if (!email) return '—';
        const handleCopy = e => {
          e.stopPropagation();
          navigator.clipboard.writeText(email).then(() => {
            toast.info('Email copied to clipboard');
          });
        };
        return (
          <div className={styles.actionCell}>
            <button
              type="button"
              className={styles.copyEmailButton}
              onClick={handleCopy}>
              <CopyOutlined />
            </button>
            <a
              href={`mailto:${email}`}
              className={styles.sendEmailButton}
              onClick={e => e.stopPropagation()}>
              <MailOutlined />
              Send Email
            </a>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.membersTableContainer}>
      <div className={styles.membersHeader}>
        <h3 className={styles.sectionTitle}>
          Daba Power Users - Active Referrers
        </h3>
      </div>
      <div className={styles.filterSection}>
        <p className={styles.filterDescription}>
          Filter users by referral activity.
        </p>
        <div className={styles.filterRow}>
          <Form.Item className={styles.filterField}>
            <Input
              placeholder="Search by username..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={styles.searchInput}
              allowClear
            />
          </Form.Item>
          <Form.Item label="Min Active" className={styles.filterField}>
            <Input
              type="number"
              min={0}
              value={minActiveReferrals}
              onChange={e => setMinActiveReferrals(Number(e.target.value) || 0)}
              placeholder="0"
              className={styles.numberInput}
            />
          </Form.Item>
          <Form.Item label="Min Inactive" className={styles.filterField}>
            <Input
              type="number"
              min={0}
              value={minInactiveReferrals}
              onChange={e =>
                setMinInactiveReferrals(Number(e.target.value) || 0)
              }
              placeholder="0"
              className={styles.numberInput}
            />
          </Form.Item>
          <Button className={styles.resetButton} onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>
      <div className={styles.paginationRowWithAmbassador}>
        <span>
          Showing {dataSource.length} of {paginationData.total ?? 0} users
        </span>
        <span>
          {ambassadorEligibleCount} users eligible for ambassador program
        </span>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey={record => record?.user?.id || Math.random()}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: paginationData.total || 0,
          showSizeChanger: true,
          position: ['bottomRight'],
        }}
        onChange={handleTableChange}
        className="antd-table"
      />
    </div>
  );
};
